import {
  FEEDBACK_PROMPT_SHOW_DELAY_MS,
  FEEDBACK_PROMPT_SNOOZE_MS,
  FEEDBACK_PROMPT_SOURCE,
  FeedbackPromptStatus,
  isDebugFeedbackPromptForced,
} from 'constants/feedback-prompt'
import { EVENTS } from 'constants/gtm'
import * as gtag from 'lib/gtag'
import {
  clearFeedbackPromptSnoozeUntil,
  setFeedbackPromptSnoozeUntil,
  setFeedbackPromptStatus,
} from 'local-store/feedback-prompt'
import { useAuthUser } from 'next-firebase-auth'
import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { saveProductFeedback } from 'utils/database/save-product-feedback'
import { isFeedbackPromptEligible } from 'utils/feedback-prompt/is-feedback-prompt-eligible'
import { waitForClearModalSlot } from 'utils/feedback-prompt/wait-for-clear-modal-slot'

const FEEDBACK_REQUIRED_MESSAGE = 'Please share a little feedback before submitting.'

const FEEDBACK_THANK_YOU_MESSAGE = 'Thank you for your feedback!'

interface UseFeedbackPromptArgs {
  finished: number | false
  wpmCalculatedAt: number
}

/**
 * Manages eligibility, display timing, and persistence for the three-day product feedback prompt.
 * Intended to run only after a completed typing test (when `finished` is set).
 */
export const useFeedbackPrompt = ({ finished, wpmCalculatedAt }: UseFeedbackPromptArgs) => {
  const forceDebugOpen = isDebugFeedbackPromptForced()
  const [isVisible, setIsVisible] = useState(forceDebugOpen)
  const authUser = useAuthUser()
  const promptedForFinishRef = useRef<number | null>(null)
  const isSubmittingRef = useRef(false)

  useEffect(() => {
    if (forceDebugOpen) return

    if (!finished) return
    if (wpmCalculatedAt < finished) return
    if (promptedForFinishRef.current === finished) return
    if (!isFeedbackPromptEligible()) return

    promptedForFinishRef.current = finished

    let cancelled = false

    const schedulePrompt = async () => {
      await new Promise((resolve) => setTimeout(resolve, FEEDBACK_PROMPT_SHOW_DELAY_MS))

      if (cancelled) return

      await waitForClearModalSlot()

      if (cancelled) return
      if (!isFeedbackPromptEligible()) return

      setIsVisible(true)

      gtag.event({ name: EVENTS.feedback_prompt_shown })
    }

    schedulePrompt()

    return () => {
      cancelled = true
    }
  }, [finished, forceDebugOpen, wpmCalculatedAt])

  const hidePrompt = useCallback(() => {
    setIsVisible(false)
  }, [])

  const handleSubmit = useCallback(
    async ({ feedback }: { feedback: string }) => {
      const trimmed = feedback.trim()

      if (!trimmed) {
        toast.error(FEEDBACK_REQUIRED_MESSAGE)
        return
      }

      if (isSubmittingRef.current) return

      isSubmittingRef.current = true

      try {
        await saveProductFeedback({
          ...(authUser.id ? { userId: authUser.id } : {}),
          ...(authUser.email ? { email: authUser.email } : {}),
          timestamp: Date.now(),
          feedback: trimmed,
          source: FEEDBACK_PROMPT_SOURCE,
        })

        setFeedbackPromptStatus({ status: FeedbackPromptStatus.SUBMITTED })
        clearFeedbackPromptSnoozeUntil()
        hidePrompt()
        toast.success(FEEDBACK_THANK_YOU_MESSAGE)
        gtag.event({ name: EVENTS.feedback_prompt_submitted })
      } finally {
        isSubmittingRef.current = false
      }
    },
    [authUser.email, authUser.id, hidePrompt]
  )

  const handleAskMeLater = useCallback(() => {
    const snoozeUntil = Date.now() + FEEDBACK_PROMPT_SNOOZE_MS

    setFeedbackPromptStatus({ status: FeedbackPromptStatus.SNOOZED })
    setFeedbackPromptSnoozeUntil({ timestamp: snoozeUntil })
    hidePrompt()
    gtag.event({ name: EVENTS.feedback_prompt_snoozed })
  }, [hidePrompt])

  const handleDismissForever = useCallback(() => {
    setFeedbackPromptStatus({ status: FeedbackPromptStatus.DISMISSED_FOREVER })
    clearFeedbackPromptSnoozeUntil()
    hidePrompt()
    gtag.event({ name: EVENTS.feedback_prompt_dismissed_forever })
  }, [hidePrompt])

  return {
    isVisible,
    handleSubmit,
    handleAskMeLater,
    handleDismissForever,
  }
}
